import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { host_url } from '../../../App';
import BackArrow from '../../../BackArrow';
import '../../../styles/CiTable.css';
import './KindDetail.css';

// Prioritaet wird als 50 (Prio 1) bis 10 (Prio 5) gespeichert
function prioNummer(prioritaet) {
    return (60 - prioritaet) / 10;
}

function formatDatum(datum) {
    if (!datum) return '';
    return new Date(datum).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
    });
}

function KindDetail() {
    const { kindId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        setError('');
        fetch(`${host_url}/kind_wunsch_uebersicht?kindId=${kindId}`)
            .then(response => {
                if (!response.ok) throw new Error('Daten für das Kind konnten nicht geladen werden.');
                return response.json();
            })
            .then(setData)
            .catch(err => setError(err.message))
            .finally(() => setIsLoading(false));
    }, [kindId]);

    if (isLoading) {
        return (
            <div>
                <div className="page-header">
                    <BackArrow to="/jobs/kinderliste" />
                    <h1>Kind wird geladen...</h1>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div>
                <div className="page-header">
                    <BackArrow to="/jobs/kinderliste" />
                    <h1>Fehler</h1>
                </div>
                <p>{error || 'Kind nicht gefunden.'}</p>
            </div>
        );
    }

    const { kind, wuensche, zuteilungen } = data;

    // Wünsche und Zuteilungen pro Einheit gruppieren
    const einheitenMap = new Map();
    const getEinheit = (row) => {
        if (!einheitenMap.has(row.EinheitID)) {
            einheitenMap.set(row.EinheitID, {
                EinheitID: row.EinheitID,
                EinheitName: row.EinheitName,
                Datum: row.Datum,
                Start: row.Start,
                wuensche: [],
                zuteilungen: [],
            });
        }
        return einheitenMap.get(row.EinheitID);
    };
    wuensche.forEach(w => getEinheit(w).wuensche.push(w));
    zuteilungen.forEach(z => getEinheit(z).zuteilungen.push(z));

    const einheiten = [...einheitenMap.values()].sort((a, b) => {
        const dateDiff = new Date(a.Datum) - new Date(b.Datum);
        if (dateDiff !== 0) return dateDiff;
        return String(a.Start || '').localeCompare(String(b.Start || ''));
    });

    return (
        <div>
            <div className="page-header">
                <BackArrow to="/jobs/kinderliste" />
                <h1 className="kind-detail-titel">
                    {kind.Name} {kind.Nachname}
                </h1>
            </div>
            <p className="kind-detail-sub">Zelt: {kind.Zeltname}</p>

            {einheiten.length === 0 && (
                <p>Für dieses Kind sind noch keine Jobwünsche oder Zuteilungen vorhanden.</p>
            )}

            {einheiten.map(einheit => {
                const zugeteilteJobIds = new Set(einheit.zuteilungen.map(z => z.JobID));
                const hatZuteilung = einheit.zuteilungen.length > 0;
                const jobsAusserhalbWuensche = einheit.zuteilungen.filter(
                    z => !einheit.wuensche.some(w => w.JobID === z.JobID)
                );

                return (
                    <div className="einheit-block" key={einheit.EinheitID}>
                        <h2 className="einheit-titel">
                            {einheit.EinheitName}
                            <span className="einheit-datum">{formatDatum(einheit.Datum)}</span>
                        </h2>

                        {jobsAusserhalbWuensche.length > 0 && (
                            <div className="zugeteilter-job-hinweis">
                                Zugeteilter Job (war nicht unter den Wünschen):{' '}
                                <strong>{jobsAusserhalbWuensche.map(z => z.JobName).join(', ')}</strong>
                            </div>
                        )}

                        {einheit.wuensche.length > 0 ? (
                            <div className="ci-table-wrapper">
                                <table className="ci-table">
                                    <thead>
                                        <tr>
                                            <th>Prio</th>
                                            <th>Jobwunsch</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {einheit.wuensche.map(wunsch => {
                                            const erfuellt = zugeteilteJobIds.has(wunsch.JobID);
                                            const rowClass = erfuellt
                                                ? 'wunsch-erfuellt'
                                                : hatZuteilung ? 'wunsch-ausgegraut' : '';
                                            return (
                                                <tr key={`${wunsch.EinheitID}-${wunsch.JobID}`} className={rowClass}>
                                                    <td>{prioNummer(wunsch.Prioritaet)}</td>
                                                    <td>
                                                        {wunsch.JobName}
                                                        {erfuellt && <span className="wunsch-haken"> ✓ bekommen</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="keine-wuensche">Keine Wünsche abgegeben.</p>
                        )}

                        {!hatZuteilung && (
                            <p className="kein-job-hinweis">Noch kein Job zugeteilt.</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default KindDetail;
